import { uvToSphereGlsl, sphereToUvGlsl } from './glslUtils';

/** Blur shader — applies a Gaussian blur to the layer base texture, masked by
 *  a brush stroke render-target.
 *
 *  Two-pass separable Gaussian: pass 0 blurs horizontally across the whole
 *  texture into a temp RT; pass 1 blurs vertically and composites the result
 *  with the original using the stroke mask.  A 25-tap kernel (±12, sigma=8.0)
 *  with step = uBlurRadius/4 texels gives smooth, gap-free blurring even at
 *  large radii, while preserving the same texel-space sigma as before.
 *
 *  Uniforms:
 *    uBase       – pass 0: source layer RT | pass 1: horizontally-blurred temp RT
 *    uOriginal   – pass 1 only: original unblurred layer RT (for final mix)
 *    uStroke     – pass 1 only: completed stroke mask RT (premultiplied, world UV)
 *    uOpacity    – brush opacity → controls blend strength (0 = no blur, 1 = full blur)
 *    uBlurRadius – Gaussian kernel spread in texels (1 = tight, ~24 = wide)
 *    uTexelSize  – vec2(1/texWidth, 1/texHeight)
 *    uRotInv     – layer bake transform (forward rotation, same as composite bake)
 *    uTranslateY – layer UV Y shift
 *    uPassMode   – 0 = horizontal blur only; 1 = vertical blur + stroke mask mix
 */

export const blurVertexShader = /*glsl*/`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

export const blurFragmentShader = /*glsl*/`
  precision highp float;
  varying vec2 vUv;

  uniform sampler2D uBase;
  uniform sampler2D uOriginal;
  uniform sampler2D uStroke;
  uniform float uOpacity;
  uniform float uBlurRadius;
  uniform vec2  uTexelSize;
  uniform mat3  uRotInv;
  uniform float uTranslateY;
  uniform int   uPassMode;

${uvToSphereGlsl}${sphereToUvGlsl}
  // Bake-mode transform: ty shift in layer-local space BEFORE forward rotation.
  vec2 transformedStrokeUV(vec2 uv) {
    vec3 p = uvToSphere(vec2(uv.x, clamp(uv.y + uTranslateY, 0.0, 1.0)));
    p = uRotInv * p;
    return sphereToUV(p);
  }

  void main() {
    // 25-tap 1D Gaussian (sigma = 8.0 in kernel-index space, step = uBlurRadius/4 texels).
    // Using step = radius/4 instead of radius keeps adjacent samples ≤radius/4 texels
    // apart, eliminating the faceted/choppy appearance at large radii while preserving
    // exactly the same sigma in texel space (8 × radius/4 = 2 × radius, same as before).
    // Pass 0: horizontal direction.  Pass 1: vertical direction.
    vec2 dir      = (uPassMode == 0) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec2 blurStep = uTexelSize * uBlurRadius * 0.25 * dir;

    vec3  accRGB = vec3(0.0);
    float accA   = 0.0;
    float totalW = 0.0;

    for (int i = -12; i <= 12; i++) {
      float fi = float(i);
      // sigma = 8.0 → exp(-fi²/128)
      float w        = exp(-fi * fi / 128.0);
      vec2  sampleUV = vUv + fi * blurStep;
      vec4  s        = texture2D(uBase, sampleUV);
      float aw       = w * s.a;
      accRGB  += s.rgb * aw;
      accA    += s.a   * w;
      totalW  += w;
    }

    float blurredA   = accA / totalW;
    vec3  blurredRGB = (accA > 0.001) ? accRGB / accA : texture2D(uBase, vUv).rgb;
    vec4  blurred    = vec4(blurredRGB, blurredA);

    if (uPassMode == 0) {
      // Horizontal pass: write fully-blurred result — masking applied in pass 1.
      gl_FragColor = blurred;
    } else {
      // Vertical pass: mix original → blurred based on stroke coverage.
      vec4  original = texture2D(uOriginal, vUv);
      vec4  smask    = texture2D(uStroke, transformedStrokeUV(vUv));
      float blend    = smask.a * uOpacity;
      gl_FragColor   = mix(original, blurred, blend);
    }
  }
`;
